// The user manual, as data. Layout lives in ManualView; this file is just the
// words, so the manual can be corrected without touching component code.
//
// Section shapes:
//   { type: 'prose',  text }
//   { type: 'steps',  title?, steps: [] }        numbered, one action each
//   { type: 'figure', id, caption }              id must match a SHOT_LIST id
//   { type: 'note',   tone: 'tip'|'warn', text }
//   { type: 'table',  head: [], rows: [[]] }
//
// Every figure renders a built-in diagram until a real screenshot with the
// matching filename is dropped into src/Assets/manual/ — see SHOT_LIST below
// and the README in that folder.

export const CHAPTERS = [
  // ── 1 ──────────────────────────────────────────────────────────────────────
  {
    id: 'start',
    title: 'Getting Started',
    blurb: 'What REKON is, how it is laid out, and the five places you work.',
    sections: [
      { type: 'prose', text: 'REKON runs the shop floor and the money that comes off it. Every truck in the building is a job. Every job moves left to right through the same seven stages, and every job carries its own estimate, parts, photos, labor and invoice. Nothing lives on a whiteboard and nothing lives in someone’s head.' },
      { type: 'prose', text: 'The whole product is five workspaces across the top bar. There is no sidebar and no hidden menu tree — if you cannot find something, it is inside one of these five.' },
      {
        type: 'table',
        head: ['Workspace', 'What it is for'],
        rows: [
          ['Flow', 'The board. Every open job, what stage it is in, what is stuck.'],
          ['Schedule', 'The calendar. What is booked and when.'],
          ['Road', 'Road service calls and dispatch.'],
          ['Money', 'Estimates waiting, work not billed, invoices not paid.'],
          ['Customers', 'Every customer and their unit history.'],
        ],
      },
      { type: 'figure', id: '01-global-header', caption: 'The top bar. Five workspaces, global search, the gear menu for Settings.' },
      { type: 'note', tone: 'tip', text: 'The number badge on Money is the count of jobs with money stuck somewhere. If it is not zero, there is cash sitting still.' },
      {
        type: 'steps',
        title: 'Signing in the first time',
        steps: [
          'Go to your REKON web address and sign in with the email and password you were issued.',
          'Click the gear icon in the top-right, then Settings.',
          'Fill in Business Profile completely — shop name, address, phone, billing email. This prints on every estimate and invoice you send a customer.',
          'Open Billing and set your default labor rate, sales tax rate and payment terms.',
          'Open Branding and upload your shop logo.',
        ],
      },
      { type: 'note', tone: 'warn', text: 'Do the Business Profile before you send anything to a customer. Until the address and phone are filled in, your estimates and invoices go out with no address to mail a check to.' },
    ],
  },

  // ── 2 ──────────────────────────────────────────────────────────────────────
  {
    id: 'board',
    title: 'The Flow Board',
    blurb: 'Where every open job lives and how work moves across the shop.',
    sections: [
      { type: 'prose', text: 'The board is the shop. Seven columns, left to right, in the order work actually happens. A job sits in exactly one column, and the column it is in is the honest answer to “where is that truck?”' },
      {
        type: 'table',
        head: ['Column', 'Means'],
        rows: [
          ['Intake', 'In the yard, not looked at yet.'],
          ['Diag', 'Being diagnosed, or waiting on a diagnosis.'],
          ['Approval', 'Waiting on the customer to say yes.'],
          ['Parts', 'Waiting on parts, or parts on hold.'],
          ['Repair', 'A tech has hands on it.'],
          ['Verify', 'Road test or QC.'],
          ['Ready', 'Work is done. Ready to invoice and go home.'],
        ],
      },
      { type: 'figure', id: '02-flow-board', caption: 'The Flow board with its seven stages.' },
      {
        type: 'steps',
        title: 'Moving a job forward',
        steps: [
          'Click the job card to open it, or drag the card to the next column.',
          'Each job has one primary button that tells you the next real move — Build Estimate, Send for Approval, Start Repair, Generate Invoice.',
          'Press it. REKON records who moved it and when.',
        ],
      },
      { type: 'prose', text: 'The same jobs can be looked at seven different ways using the lens switcher in the board toolbar. It is the same live data every time, just arranged for a different question.' },
      {
        type: 'table',
        head: ['Lens', 'Answers'],
        rows: [
          ['Board', 'Where is everything?'],
          ['List', 'Give me a flat, sortable list.'],
          ['Today', 'What has to happen today?'],
          ['Priority', 'What do I touch next?'],
          ['Timeline', 'How long has this been going on?'],
          ['Shop', 'Which bay is that truck in?'],
          ['Team', 'What is each tech on right now?'],
        ],
      },
      { type: 'figure', id: '03-job-card', caption: 'A job card: RO number, customer, unit, how long it has been sitting, and any blocker.' },
      { type: 'note', tone: 'tip', text: 'A card turns colour when it has been sitting in one stage longer than that stage should take. You are looking for colour, not reading every card.' },
    ],
  },

  // ── 3 ──────────────────────────────────────────────────────────────────────
  {
    id: 'job',
    title: 'Working a Job',
    blurb: 'The job page — the single screen where a repair order actually gets worked.',
    sections: [
      { type: 'prose', text: 'Click any card and you land on the job page. Everything about that truck is here: the story, the photos, the parts, the labor, the money. You should not need a second window.' },
      { type: 'figure', id: '04-job-page', caption: 'The job page. Lifecycle rail across the top, activity and details on the left, parts and money on the right.' },
      { type: 'prose', text: 'Across the top is the lifecycle rail — eight stops from Intake to Paid. It blends the shop workflow and the money loop, because they are really one job. Warranty and internal jobs drop the money stops automatically; there is no invoice coming, so REKON does not pretend there is.' },
      {
        type: 'steps',
        title: 'Documenting the work',
        steps: [
          'Use the Activity tab to log what happened, in plain language. This is the running story of the job.',
          'Use Findings for what the tech actually discovered. Findings feed the Cause line on the estimate automatically.',
          'Use Photos for anything you would want in your hand during an argument about the bill. Photos taken on the phone land here.',
          'Use the Parts tab to add every part with quantity and price. Parts you add here are the parts you can put on the estimate.',
        ],
      },
      { type: 'note', tone: 'tip', text: 'Write the finding once, on the job, and it flows to the estimate and then to the invoice. Anything you type twice is a place the paperwork can disagree with itself.' },
      {
        type: 'steps',
        title: 'Flagging a blocker',
        steps: [
          'If the job is stuck — waiting on a customer, a part, a tow, an authorization — mark the blocker on the job.',
          'The card on the board shows the blocker so nobody has to ask.',
          'Clear the blocker when it is resolved. The clock on that stall stops.',
        ],
      },
    ],
  },

  // ── 4 ──────────────────────────────────────────────────────────────────────
  {
    id: 'estimate',
    title: 'Building an Estimate',
    blurb: 'Turning diagnosed work into a priced document the customer can approve.',
    sections: [
      { type: 'prose', text: 'An estimate is built out of operations, not a flat list of lines. One operation is one job on the truck — an air leak, an annual inspection, a PM. Each operation carries its own story, its own labor and its own parts. That is what lets one invoice cover three unrelated repairs without turning into mush.' },
      {
        type: 'steps',
        title: 'Build it',
        steps: [
          'Open the job and press Build Estimate.',
          'Name the operation — for example, "Air Leak – BR015".',
          'Fill in Complaint, Cause and Correction. Complaint pre-fills from the reason the truck came in; Cause and Correction pre-fill from the tech’s findings and repair notes.',
          'Add labor: either hours × rate, or a flat price for flat-rate work. The rate pre-fills from your Billing settings.',
          'Tick the parts that belong to this operation. Only parts already on the job appear here.',
          'If the operation is tax exempt — a state inspection, for example — tick Tax Exempt on that operation only.',
          'Press Add Operation to start the next one, and repeat.',
          'Set shop supplies, either as a flat dollar amount or a percentage of labor.',
          'Check the total, then switch to Preview to see exactly what the customer will see.',
        ],
      },
      { type: 'figure', id: '05-estimate-builder', caption: 'The estimate builder. Operations on the left, running totals on the right.' },
      { type: 'note', tone: 'tip', text: 'Tax exempt is set per operation, not per estimate. A DOT inspection and a brake job can ride on the same invoice with only the brake job taxed.' },
      { type: 'figure', id: '06-estimate-preview', caption: 'Preview — the customer-facing document, with your shop name, address and logo.' },
      { type: 'prose', text: 'Every dollar figure in REKON — the builder, the one-click invoice, and all three QuickBooks files — is calculated by the same piece of code. The preview total, the invoice total and the number that lands in QuickBooks cannot drift apart.' },
    ],
  },

  // ── 5 ──────────────────────────────────────────────────────────────────────
  {
    id: 'approval',
    title: 'Getting It Approved',
    blurb: 'Sending the estimate and capturing a real, timestamped yes.',
    sections: [
      {
        type: 'steps',
        title: 'Send it to the customer',
        steps: [
          'From the estimate, press Send for Approval.',
          'REKON creates a private link for this estimate, copies it to your clipboard, and opens an email addressed to the customer with the estimate written out in the body.',
          'Send the email — or paste the link into a text message instead.',
        ],
      },
      { type: 'prose', text: 'The customer opens the link on their phone. No app, no login, no account. They see the operations, the parts, the labor and the total, and they get two buttons: Approve and Decline.' },
      { type: 'figure', id: '07-approval-page', caption: 'What the customer sees when they open the approval link on their phone.' },
      { type: 'prose', text: 'When they approve, REKON stamps the job with who approved it, how, and at what dollar figure, and writes that into the job’s permanent activity record. That is your authorization. If they decline, the reason is captured and the job drops into the Unapproved queue so it does not quietly die.' },
      {
        type: 'steps',
        title: 'When they approve over the phone instead',
        steps: [
          'Open the estimate and press Record Approval.',
          'Choose the method — phone, in person, email, purchase order.',
          'Type who authorized it. Use their actual name.',
          'Save. It is logged the same way an online approval is.',
        ],
      },
      { type: 'note', tone: 'warn', text: '"The customer said yes" is not an authorization. A name, a method and a timestamp is. Record it every time — it costs ten seconds and it is the only thing that helps you in a dispute.' },
    ],
  },

  // ── 6 ──────────────────────────────────────────────────────────────────────
  {
    id: 'invoice',
    title: 'Invoicing',
    blurb: 'Turning finished work into an invoice without retyping anything.',
    sections: [
      { type: 'prose', text: 'There are two ways to invoice. They produce identical numbers, so use whichever fits what you are doing.' },
      {
        type: 'steps',
        title: 'From the job',
        steps: [
          'Open the job. When the work is done, the primary button reads Generate Invoice.',
          'Press it. The approved estimate becomes an invoice — same operations, same parts, same labor.',
          'REKON stamps the invoice date, applies your payment terms and calculates the due date.',
        ],
      },
      {
        type: 'steps',
        title: 'From the Money workspace — faster for a batch',
        steps: [
          'Go to Money and open the Unbilled tab. Every finished job with no invoice is listed with a dollar figure.',
          'Press Generate invoice on a row.',
          'Confirm the amount when REKON asks.',
          'The invoice is saved and your QuickBooks file downloads in the same motion.',
        ],
      },
      { type: 'figure', id: '08-money-unbilled', caption: 'Money → Unbilled. Finished work that has not been billed, with one-click invoicing.' },
      { type: 'note', tone: 'tip', text: 'If a job was finished without anyone entering labor, REKON estimates the labor from the planned repair categories and your default rate, and tells you it did so in the confirmation. Review those before sending.' },
      { type: 'prose', text: 'Invoice numbers are built from the RO — INV-1042-1. Until a payment is recorded you can press Undo Invoice and go back to an editable estimate; nothing is deleted.' },
    ],
  },

  // ── 7 ──────────────────────────────────────────────────────────────────────
  {
    id: 'quickbooks',
    title: 'Exporting to QuickBooks',
    blurb: 'Getting invoices out of REKON and into QuickBooks Online.',
    sections: [
      { type: 'note', tone: 'warn', text: 'Do the setup below once, before your first import. Skipping it makes QuickBooks create duplicate products and services, and cleaning that up is genuinely unpleasant.' },
      {
        type: 'steps',
        title: 'One-time setup',
        steps: [
          'In QuickBooks, look at your Products and Services list and write down the exact names you use for labor, parts, shop supplies and sales tax.',
          'In REKON, go to Settings → Billing.',
          'Set "Which QuickBooks do you run?" to QuickBooks Online.',
          'Leave Sales Tax on Import set to "Let QuickBooks calculate it" unless your QuickBooks company is not on Automated Sales Tax.',
          'Type your exact QuickBooks names into the Product / Service Names fields.',
          'Save.',
        ],
      },
      { type: 'figure', id: '09-settings-billing', caption: 'Settings → Billing, QuickBooks section.' },
      {
        type: 'steps',
        title: 'Export from REKON',
        steps: [
          'For one invoice: open the job, open the invoice, press Export to QuickBooks and choose QuickBooks Online (CSV).',
          'For everything open: go to Money → Unpaid and press Export all open invoices.',
          'The file downloads. If you had more than 100 invoices, you get several files named 1of3, 2of3 and so on — import all of them.',
        ],
      },
      {
        type: 'steps',
        title: 'Import into QuickBooks Online',
        steps: [
          'In QuickBooks, click the gear icon, then Import data, then Invoices.',
          'Upload the CSV file REKON gave you.',
          'On the mapping screen, check that the columns line up. They should match automatically.',
          'Set the date format to MM/DD/YYYY.',
          'Click Next, review the invoices QuickBooks is about to create, and click Import.',
        ],
      },
      { type: 'figure', id: '10-qbo-import', caption: 'The QuickBooks Online import screen with a REKON file loaded.' },
      { type: 'note', tone: 'tip', text: 'Run one invoice through first and look at the result in QuickBooks before you push a whole batch. Check that it matched your existing Labor and Parts items instead of creating new ones.' },
      { type: 'prose', text: 'Two things REKON tells you before you import, when they apply. Discounts: QuickBooks Online will not accept a negative line, so REKON folds the discount into the largest line on the invoice and notes it in that line’s description — the total is unchanged. Sales tax: in the default setting REKON marks each line taxable or not and lets QuickBooks calculate the tax, so the figure QuickBooks lands on should match your invoice. Check it the first time.' },
      { type: 'note', tone: 'warn', text: 'This is a file import, not a live connection. Nothing syncs back from QuickBooks. If your bookkeeper edits an invoice in QuickBooks, REKON will not know.' },
    ],
  },

  // ── 8 ──────────────────────────────────────────────────────────────────────
  {
    id: 'paid',
    title: 'Getting Paid',
    blurb: 'Recording payments and chasing the ones that have not come in.',
    sections: [
      { type: 'prose', text: 'Money → Unpaid is every invoice with an open balance, oldest first, aged against your payment terms.' },
      {
        type: 'steps',
        title: 'Record a payment',
        steps: [
          'Go to Money → Unpaid and find the invoice.',
          'Press Record Payment.',
          'Enter the amount. Leave it as-is to pay the invoice in full, or type a smaller number for a partial payment or a deposit.',
          'Choose the method — check, card, cash, ACH, fleet account.',
          'Type the check number or confirmation code in the reference field.',
          'Save. The remaining balance updates, and the invoice flips to Paid on its own when the balance reaches zero.',
        ],
      },
      { type: 'figure', id: '11-collections', caption: 'Money → Unpaid, with a payment being recorded.' },
      {
        type: 'steps',
        title: 'Chase a late one',
        steps: [
          'Find the overdue invoice in Money → Unpaid.',
          'Press the payment reminder button. Your texting app opens with the customer’s number and the balance already filled in.',
          'Read it, then send it.',
        ],
      },
      { type: 'note', tone: 'tip', text: 'Partial payments are fully supported. Record the deposit when you take it — the invoice keeps showing exactly what is still owed.' },
    ],
  },

  // ── 9 ──────────────────────────────────────────────────────────────────────
  {
    id: 'money',
    title: 'The Money Workspace',
    blurb: 'The three places money gets stuck, and the reports behind them.',
    sections: [
      { type: 'prose', text: 'Money is not a report you go read. It is a work queue. Three tabs, and each one is a list of things somebody needs to do today.' },
      {
        type: 'table',
        head: ['Tab', 'What is in it', 'What to do'],
        rows: [
          ['Unapproved', 'Estimate sent, customer has not answered — or declined it.', 'Resend the text, copy the link, or call.'],
          ['Unbilled', 'Work finished, no invoice. Including jobs with no estimate at all.', 'Generate the invoice.'],
          ['Unpaid', 'Invoiced, balance still open.', 'Record the payment or send a reminder.'],
        ],
      },
      { type: 'figure', id: '12-money-queues', caption: 'The Money workspace and its three queues.' },
      { type: 'note', tone: 'tip', text: 'Unbilled catches jobs that were finished with no paperwork at all — no estimate, nothing. That is the money that used to walk out the door. Check it before you go home.' },
      { type: 'prose', text: 'Two more tabs sit alongside them. Reports shows cycle time — how long jobs actually take, stage by stage, so you can see where the shop loses days. Analytics shows throughput and workload across the shop.' },
    ],
  },

  // ── 10 ─────────────────────────────────────────────────────────────────────
  {
    id: 'settings',
    title: 'Settings',
    blurb: 'Everything you configure once, in the gear menu.',
    sections: [
      {
        type: 'table',
        head: ['Tab', 'What lives there'],
        rows: [
          ['Business Profile', 'Shop name, address, phone, billing email, website, time zone. Prints on customer paperwork.'],
          ['Billing', 'Labor rate, sales tax rate, payment terms, invoice disclaimer, and all QuickBooks export settings.'],
          ['Branding', 'Your logo and the app colours.'],
          ['Flow Layout', 'Rename or hide board columns to match how your shop actually talks.'],
          ['Archive', 'How long job records and photos are kept.'],
          ['Service Catalog', 'Reusable labor operations and standard jobs, so common work is one click.'],
          ['User Manual', 'This document.'],
        ],
      },
      { type: 'figure', id: '13-settings', caption: 'Settings, with the tab list down the side.' },
      { type: 'note', tone: 'warn', text: 'Settings changes apply to the whole shop, not just you. The invoice disclaimer in particular goes out on every customer document — have someone read it before you change it.' },
    ],
  },

  // ── 11 ─────────────────────────────────────────────────────────────────────
  {
    id: 'mobile',
    title: 'Phones and Tablets',
    blurb: 'What technicians do from the floor.',
    sections: [
      { type: 'prose', text: 'REKON works on a phone. Technicians do not need the full board — they need their jobs, and a way to put what they found into the system without walking to the office.' },
      {
        type: 'steps',
        title: 'What a tech does from the floor',
        steps: [
          'Open REKON in the phone’s browser and sign in.',
          'Open the assigned job from the jobs list.',
          'Log findings as they are discovered, in plain language.',
          'Take photos straight into the job.',
          'Add parts used.',
          'Move the job to the next stage when the work is genuinely done.',
        ],
      },
      { type: 'figure', id: '14-mobile', caption: 'The job page on a phone.' },
      { type: 'note', tone: 'tip', text: 'Findings typed by the tech on the floor pre-fill the Cause line on the estimate. The better the finding, the less the writer has to invent later.' },
    ],
  },

  // ── 12 ─────────────────────────────────────────────────────────────────────
  {
    id: 'faq',
    title: 'Common Questions',
    blurb: 'The things people ask in the first week.',
    sections: [
      {
        type: 'table',
        head: ['Question', 'Answer'],
        rows: [
          ['Can I start a repair before the estimate is approved?', 'Yes. REKON asks once, lets you through, and writes the override onto the job so there is a record of who decided.'],
          ['Can I close a job without invoicing it?', 'Yes, and it is logged. The job then sits in Money → Unbilled until somebody invoices it.'],
          ['I invoiced the wrong job. Can I undo it?', 'Yes — press Undo Invoice. It goes back to an editable estimate. This is available until a payment has been recorded against it.'],
          ['Does REKON text customers automatically?', 'No. The text buttons open your own phone’s messaging app with the message already written. You press send.'],
          ['Does the invoice email have a PDF attached?', 'Not yet. The email opens with the invoice written out in the body. To send a PDF, use Print and choose Save as PDF, then attach it yourself.'],
          ['Does REKON sync with QuickBooks live?', 'No. You export a file and import it into QuickBooks. Nothing comes back from QuickBooks.'],
          ['A customer wants to change an approved estimate.', 'Edit the estimate and send it for approval again. It becomes a new version and the customer approves the new figure.'],
          ['Two people edited the same job at once.', 'Last write wins. REKON keeps every change in the activity record, so you can see what happened and who did it.'],
        ],
      },
    ],
  },
];

// ─── Screenshot slots ─────────────────────────────────────────────────────────

// Every figure id above, with exactly what to capture. Drop a PNG named
// <id>.png into src/Assets/manual/ and it replaces the built-in diagram
// automatically — no code change.
export const SHOT_LIST = [
  { id: '01-global-header', screen: 'Any screen', capture: 'Just the top bar. Crop to the header strip only — the five workspace tabs, search, gear and user icons.' },
  { id: '02-flow-board',    screen: 'Flow → Board', capture: 'The whole board with jobs in most columns. Full width.' },
  { id: '03-job-card',      screen: 'Flow → Board', capture: 'One single job card, cropped tight. Pick one with a blocker or an idle colour showing.' },
  { id: '04-job-page',      screen: 'Any job', capture: 'The full job page on a job that has activity, parts and an estimate. Show the lifecycle rail at the top.' },
  { id: '05-estimate-builder', screen: 'Job → Estimate', capture: 'The builder with two operations filled in, labor and parts on both, and a total showing.' },
  { id: '06-estimate-preview', screen: 'Job → Estimate → Preview', capture: 'The customer-facing document. Fill in Business Profile and upload the logo first so it looks real.' },
  { id: '07-approval-page', screen: 'The public /estimate/ link', capture: 'Open the approval link on a phone and screenshot it. Portrait.' },
  { id: '08-money-unbilled', screen: 'Money → Unbilled', capture: 'The queue with at least three rows and the Generate invoice buttons visible.' },
  { id: '09-settings-billing', screen: 'Settings → Billing', capture: 'Scrolled to the QuickBooks Export section, with the format dropdown and the product name fields.' },
  { id: '10-qbo-import',    screen: 'QuickBooks Online', capture: 'QuickBooks’ own import screen with a REKON CSV loaded at the mapping step. Taken in QuickBooks, not REKON.' },
  { id: '11-collections',   screen: 'Money → Unpaid', capture: 'The queue with the Record Payment form open on one row.' },
  { id: '12-money-queues',  screen: 'Money', capture: 'The Money workspace showing the tab strip with counts on Unapproved, Unbilled and Unpaid.' },
  { id: '13-settings',      screen: 'Settings', capture: 'Any Settings tab, showing the full tab list.' },
  { id: '14-mobile',        screen: 'A job, on a phone', capture: 'Portrait phone screenshot of the job page.' },
];
